import React, { useState } from 'react';
import { Table, Search, Select, Button } from 'ui';
import { useT } from 'hooks/Locale/useT';
import FlexRow from 'components/FlexRow';
import { config } from 'config/config';
import { EntitiesStatusEnum } from 'components/Modals/Filter/types';
import useQueryApiClient from 'utils/useApiClient';

const Access: React.FC = ({}) => {
    const [reload, setReload] = useState<boolean>(false);
    const [searchString, setSearchString] = useState<string>('');
    const [selectedCustomers, setSelectedCustomers] = useState<Record<number, any[]>>({});
    const [editingRows, setEditingRows] = useState<Record<number, boolean>>({});
    const [initialLoadCompleted, setInitialLoadCompleted] = useState<Record<number, boolean>>({});
    const t = useT();

    const { appendData } = useQueryApiClient({
        request: {
            disableOnMount: true,
            url: '/api/v1/users/custom-fields/:id/access',
            method: 'POST',
        },
        onSuccess: () => {
            setReload(true);
        },
    });

    const columns = [
        {
            title: t('system.field_name'),
            dataIndex: 'title',
        },
        {
            title: t('system.slug'),
            dataIndex: 'slug',
        },
        {
            title: t('system.users_filters_customer_access'),
            key: 'id',
            render: (_value: number, data: any) => {
                const rowId = data.id;

                const getInitialValue = () => {
                    if (selectedCustomers[rowId]) {
                        return selectedCustomers[rowId];
                    }

                    if (data.access && Array.isArray(data.access) && data.access.length > 0) {
                        return data.access
                            .map((item: any) => item.customerId)
                            .filter((id: any) => id != null);
                    }

                    return [];
                };

                const initialValue = getInitialValue();
                const isEditing = editingRows[rowId] || false;

                const handleSelectChange = (selectedValues: any) => {
                    setSelectedCustomers((prev) => ({
                        ...prev,
                        [rowId]: selectedValues || [],
                    }));
                };

                const handleDropdownVisibleChange = (open: boolean) => {
                    if (open && !initialLoadCompleted[rowId] && initialValue.length > 0) {
                        setInitialLoadCompleted((prev) => ({
                            ...prev,
                            [rowId]: true,
                        }));
                    }
                };

                const handleEdit = () => {
                    setEditingRows((prev) => ({ ...prev, [rowId]: true }));
                };

                const handleSave = () => {
                    const selection = selectedCustomers[rowId] || initialValue;
                    const customerIds = Array.isArray(selection)
                        ? selection.map((item: any) =>
                              typeof item === 'object' ? item.value || item.id : item
                          )
                        : [];

                    appendData({ customerId: customerIds }, { id: rowId });
                    setEditingRows((prev) => ({ ...prev, [rowId]: false }));
                };

                return (
                    <div className='flex gap-5'>
                        <Select
                            label='general.customer'
                            name={`customer_${rowId}`}
                            customLayout={{}}
                            selectClassName='w-100'
                            mode='multiple'
                            url={config.api.routes.customers}
                            showSearch={true}
                            disableOnMount={!(initialValue.length > 0)}
                            method='POST'
                            params={{
                                status: [EntitiesStatusEnum.Active],
                                limit: 5,
                                select: ['name', 'id'],
                            }}
                            onDropdownVisibleChange={handleDropdownVisibleChange}
                            allowClear={true}
                            valueKey='id'
                            labelKey='name'
                            dataKey='data'
                            isResponseArray={true}
                            isFormItem={false}
                            initialFilter={initialValue}
                            value={initialValue}
                            onChange={handleSelectChange}
                            disabled={!isEditing}
                        />
                        {!isEditing ? (
                            <Button type='link' onClick={handleEdit} message='general.edit' />
                        ) : (
                            <Button type='link' onClick={handleSave} message='general.save' />
                        )}
                    </div>
                );
            },
        },
    ];

    return (
        <>
            <FlexRow
                right={
                    <div className='search'>
                        <Search setSearchString={setSearchString} setReload={setReload} />
                    </div>
                }
            />
            <Table
                columns={columns}
                url={'/api/v1/users/custom-fields'}
                setReload={setReload}
                reload={reload}
                rowKey={'id'}
                rowSelection={false}
                search={searchString}
                scroll={{ x: true }}
            />
        </>
    );
};

export default Access;
